import {
  Box,
  Collapse,
  type CollapseProps,
  Paper,
  type PaperProps,
  Stack,
  type StackProps,
  type Theme,
} from "@mui/material";
import clsx from "clsx";
import type React from "react";
import { useCallback, useState } from "react";
import { PaperCollapseStack, paperCollapseClasses } from "./styles";

interface IOpenIcon {
  open: React.ReactNode;
  close: React.ReactNode;
}
export interface ICategory {
  icon: React.ReactNode;
  iconBackgroundColor: string | ((theme: Theme) => string);
}
export interface IPaperCollapseProps extends Omit<StackProps, "title"> {
  fullWidth?: boolean;
  open?: boolean;
  onOpen?: (v: boolean) => void;
  openIcon?: IOpenIcon;
  title?: React.ReactNode;
  actionButtons?: React.ReactNode;
  category?: ICategory;
  disabled?: boolean;
  ButtonProps?: PaperProps;
  CollapseProps?: CollapseProps & PaperProps;
}

export const PaperCollapse = ({
  fullWidth,
  open,
  onOpen,
  openIcon,
  title,
  actionButtons,
  category,
  disabled,
  ButtonProps,
  CollapseProps,
  children,
  ...props
}: IPaperCollapseProps) => {
  const [innerOpen, setInnerOpen] = useState(false);

  const isOpen = typeof open === "boolean" ? open : innerOpen;

  const handlePaperClick = useCallback(() => {
    if (!disabled) {
      if (typeof open !== "boolean") {
        setInnerOpen((innerOpen) => {
          if (onOpen) {
            onOpen(!innerOpen);
          }
          return !innerOpen;
        });
      } else if (onOpen) {
        onOpen(!open);
      }
    }
  }, [open, onOpen, disabled]);

  return (
    <PaperCollapseStack {...props} fullWidth={!!fullWidth} category={category}>
      <Paper
        {...ButtonProps}
        role="button"
        className={clsx(paperCollapseClasses.paperButton, {
          [paperCollapseClasses.paperButtonUncontrolled]: onOpen === undefined,
          [paperCollapseClasses.paperButtonOpen]: isOpen,
          [paperCollapseClasses.paperButtonDisabled]: disabled,
        })}
        onClick={handlePaperClick}
      >
        <Stack className={paperCollapseClasses.paperButtonContent} direction="row">
          <Stack className={paperCollapseClasses.paperButtonTitle} direction="row">
            {openIcon && (isOpen ? openIcon.close : openIcon.open)}
            <Box
              sx={{
                py: 1,
              }}
            >
              {title}
            </Box>
          </Stack>
          <Stack direction="row">{actionButtons}</Stack>
        </Stack>
        {category && <Box className={paperCollapseClasses.category}>{category.icon}</Box>}
      </Paper>
      <Collapse
        {...CollapseProps}
        className={paperCollapseClasses.collapse}
        component={Paper}
        in={isOpen}
        timeout={300}
        orientation="vertical"
      >
        <Box
          sx={{
            p: 2,
          }}
        >
          {children}
        </Box>
      </Collapse>
    </PaperCollapseStack>
  );
};

export { paperCollapseClasses };
